import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import GuideLayout, { SectionCTA } from '../../components/GuideLayout';

const s = {
  breadcrumb: { fontSize: 13, color: '#94a3b8', marginBottom: 20 },
  breadcrumbLink: { color: '#2563eb', textDecoration: 'none' },
  h1: { fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.4px' },
  lead: { fontSize: 17, color: '#475569', lineHeight: 1.7, marginBottom: 32, borderBottom: '1px solid #e2e8f0', paddingBottom: 32 },
  searchWrap: { marginBottom: 24 },
  search: {
    width: '100%', padding: '10px 14px', fontSize: 15,
    border: '1px solid #d1d5db', borderRadius: 8, outline: 'none',
    color: '#1e293b', boxSizing: 'border-box',
  },
  term: {
    borderBottom: '1px solid #f1f5f9', paddingBottom: 18, marginBottom: 18,
  },
  termName: { fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 4 },
  termAbbr: { fontSize: 13, color: '#64748b', marginBottom: 8 },
  termDef: { fontSize: 14, color: '#475569', lineHeight: 1.7 },
  noResults: { fontSize: 14, color: '#94a3b8', padding: '20px 0' },
  nav: { display: 'flex', justifyContent: 'space-between', marginTop: 48, paddingTop: 24, borderTop: '1px solid #e2e8f0' },
  prevLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
};

const terms = [
  {
    term: 'Above threshold',
    def: 'A contract whose estimated value exceeds the EU procurement thresholds. Above-threshold contracts must follow the full EU procedure and be published EU-wide.',
  },
  {
    term: 'Award criteria',
    def: 'The criteria used to evaluate and score bids — including their relative weighting. Must be published in the contract notice before bids are submitted and cannot be changed mid-process.',
  },
  {
    term: 'BAFO',
    abbr: 'Best and Final Offer',
    def: 'A stage used in some negotiated procedures where shortlisted bidders are invited to submit their best price and terms after earlier rounds of dialogue or negotiation.',
  },
  {
    term: 'Below threshold',
    def: 'A contract below the EU thresholds. These are still often subject to national procurement rules, but are not required to be published EU-wide.',
  },
  {
    term: 'Competitive dialogue',
    def: 'A procurement procedure used for particularly complex contracts where the buyer is not able to specify the solution in advance. Buyers conduct structured dialogue with shortlisted bidders before inviting final tenders.',
  },
  {
    term: 'Consortium bid',
    def: 'A joint bid submitted by two or more companies acting together. One member acts as lead bidder. Permitted under EU rules, often used when no single company has the full capability or financial standing required.',
  },
  {
    term: 'Contract notice',
    def: 'The official publication announcing a procurement opportunity. It sets out what is being procured, who is procuring it, the estimated value, and the deadline to respond.',
  },
  {
    term: 'Contracting authority',
    def: 'The public body running the procurement — a ministry, municipality, hospital, university, or any other entity subject to procurement law. Sometimes called the "buyer" or "client".',
  },
  {
    term: 'CPV codes',
    abbr: 'Common Procurement Vocabulary',
    def: 'A standardised EU classification system for contract subjects. Every published contract is assigned one or more CPV codes — eight-digit numbers that categorise the type of goods, services, or works. For example, 72000000 = IT services; 45000000 = construction works. Used to search and filter contracts by type.',
  },
  {
    term: 'DPS',
    abbr: 'Dynamic Purchasing System',
    def: 'An open procurement arrangement similar to a framework agreement, but with no fixed end date and no limit on the number of suppliers who can join. New suppliers can apply to join at any time. Individual contracts are awarded via mini-competitions among DPS members.',
  },
  {
    term: 'ESPD',
    abbr: 'European Single Procurement Document',
    def: 'A standard self-declaration form used in EU procurement. Bidders declare that they meet the exclusion criteria and selection requirements. The buyer only checks the actual evidence from the winning bidder before contract signature.',
  },
  {
    term: 'Evaluation criteria',
    def: 'The factors against which bids are scored. Typically split into quality criteria (methodology, team, experience) and price, with published weightings. Must be specified in advance and applied consistently.',
  },
  {
    term: 'Framework agreement',
    def: 'A pre-established contract with one or more suppliers setting out the terms for future call-off contracts. Being on a framework means you are pre-qualified; individual contracts are placed without a full re-competition, or via mini-competitions among framework members.',
  },
  {
    term: 'ITT',
    abbr: 'Invitation to Tender',
    def: 'The formal tender pack issued to invited or shortlisted bidders (in restricted procedures) or to the open market (in open procedures). Contains the full specification, pricing schedule, evaluation criteria, and contract terms.',
  },
  {
    term: 'Lot',
    def: 'A subdivision of a larger procurement. Contracting authorities often divide contracts into lots to allow smaller suppliers to bid for part of the work, or to separate different types of work. Bidders may be permitted to bid for one, some, or all lots.',
  },
  {
    term: 'MEAT',
    abbr: 'Most Economically Advantageous Tender',
    def: 'The EU requirement that contracts be awarded on the basis of the best value for money — a combination of quality and price — rather than on price alone. The weighting between quality and price must be stated in advance.',
  },
  {
    term: 'Mini-competition',
    def: 'A further competition run among framework agreement or DPS members for individual call-off contracts. Used when the framework terms do not fix all the elements needed for a specific contract.',
  },
  {
    term: 'OJEU',
    abbr: 'Official Journal of the European Union',
    def: 'The official gazette of the EU, in which above-threshold procurement notices must be published. The online platform through which OJEU notices are published and searchable is TED (Tenders Electronic Daily).',
  },
  {
    term: 'Open procedure',
    def: 'A single-stage procurement procedure in which any eligible supplier can submit a full bid in response to the contract notice. The most transparent and common procedure type for above-threshold contracts.',
  },
  {
    term: 'PIN',
    abbr: 'Prior Information Notice',
    def: 'An optional advance notice published before a formal procurement begins, signalling that a contract opportunity is coming. Allows suppliers to prepare and can be used to reduce the statutory tender period.',
  },
  {
    term: 'PQQ / SQ',
    abbr: 'Pre-Qualification Questionnaire / Selection Questionnaire',
    def: 'A document used in restricted procedures to shortlist suppliers before inviting full tenders. Assesses financial standing, relevant experience, and capability. The SQ is the standardised UK version; the EU uses the ESPD for the same purpose.',
  },
  {
    term: 'Restricted procedure',
    def: 'A two-stage procurement where the buyer first shortlists candidates (via PQQ/ESPD), then issues the full ITT only to shortlisted suppliers. Used for more complex contracts where evaluating a large number of full bids would be impractical.',
  },
  {
    term: 'Standstill period',
    def: 'A mandatory pause of at least 10 calendar days between notifying the contract award decision and signing the contract. Required by EU law for above-threshold contracts, giving unsuccessful bidders the opportunity to challenge the decision before it becomes final.',
  },
  {
    term: 'TED',
    abbr: 'Tenders Electronic Daily',
    def: 'The online supplement to the OJEU and the main European platform for above-threshold public procurement notices. Publishes hundreds of thousands of contract notices each year from across the EU and beyond.',
  },
  {
    term: 'TUPE',
    abbr: 'Transfer of Undertakings (Protection of Employment)',
    def: 'UK and EU employment law provisions that protect employees when a contract changes hands from one supplier to another. Relevant for service contracts where incumbent staff would transfer to a new provider.',
  },
];

export default function Glossary() {
  const [query, setQuery] = useState('');

  const filtered = terms.filter((t) => {
    const q = query.toLowerCase();
    return (
      t.term.toLowerCase().includes(q) ||
      (t.abbr || '').toLowerCase().includes(q) ||
      t.def.toLowerCase().includes(q)
    );
  });

  return (
    <GuideLayout>
      <div style={s.breadcrumb}>
        <Link to="/guide" style={s.breadcrumbLink}>Tender Guide</Link> &rsaquo; Glossary
      </div>

      <h1 style={s.h1}>Procurement glossary</h1>
      <p style={s.lead}>
        Plain-English definitions for the terms you will encounter when reading contract notices, tender documents, and procurement regulations. {terms.length} entries, A&ndash;T.
      </p>

      <div style={s.searchWrap}>
        <input
          style={s.search}
          type="text"
          placeholder="Search terms..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={s.noResults}>No terms match "{query}"</div>
      ) : (
        filtered.map((t) => (
          <div key={t.term} style={s.term}>
            <div style={s.termName}>{t.term}</div>
            {t.abbr && <div style={s.termAbbr}>{t.abbr}</div>}
            <div style={s.termDef}>{t.def}</div>
          </div>
        ))
      )}

      <SectionCTA />

      <div style={s.nav}>
        <Link to="/guide/eu-rules" style={s.prevLink}>&larr; EU procurement rules</Link>
        <span />
      </div>
    </GuideLayout>
  );
}
